import Phaser from 'phaser';
import { GAME_WIDTH } from '@/game/config';
import { sealFromPhaser } from '@/game/ui/domInput';

/**
 * The dismissible card the end-of-game prompts are built from (the web buy card and the Android
 * rating ask). A native HTML element in Phaser's DOM layer — like the menu's controls, because
 * canvas hit-testing drops taps on touch, and because the card carries real `<a>` store links.
 *
 * Deliberately NOT modal: the DOM container is click-through except for the card itself, so the
 * end-of-game screen behind it stays fully playable — every button under it still works and any
 * tap outside it does whatever that screen normally does. The card is an interruption the player
 * can ignore, not a gate.
 */
export interface PromoCardAction {
  label: string;
  kind: 'primary' | 'ghost';
  /** Runs before the card is dismissed. Omit for a plain dismiss. */
  onClick?: () => void;
  /**
   * Leave the card up after the click — for an action that operates ON the card rather than
   * answering it (the info card's "read it again" button). Every card still needs an action
   * without this, or there is no way to close it.
   */
  keepOpen?: boolean;
}

export interface PromoCardSpec {
  title: string;
  /** Body lines, most important first — rendered as separate paragraphs. */
  lines: string[];
  /** Optional block between the lines and the actions (e.g. the store-badge row). */
  extra?: HTMLElement;
  actions: PromoCardAction[];
}

export interface PromoCardOptions {
  /** Wait before showing, so the screen's own animation (stars, score) plays first. */
  delayMs?: number;
  /**
   * An extra class on the card, for a variant that needs its own LAYOUT rather than its own size.
   *
   * There is exactly one today and it is worth saying why it could not be solved with `size`. The
   * credentials outro card carries a QR code, and a 104-unit square added to the foot of a card
   * already at its height budget goes straight through the Replay button — the failure this file's
   * placement rule exists to prevent. The same square in a second COLUMN costs no height at all
   * (`.promo-card--qr-side`), which is a layout question and not a size one: dropping to `md` to
   * make room would shrink the copy on a desktop screen with room to spare.
   */
  className?: string;
  /** Vertical centre in game units. Defaults to a spot that clears the usual bottom buttons. */
  y?: number;
  /**
   * Also dismiss on a tap anywhere outside the card. OFF by default, and deliberately so for the
   * end-of-game prompts: they are an offer the player may ignore while carrying on with the screen
   * behind them, and closing on the first stray tap would make them easy to miss. A panel the
   * player *opened* (the menu's info card) is the opposite case — tapping away is how you put it
   * down — so that one opts in.
   */
  closeOnOutsideTap?: boolean;
  /** Runs when the card goes away by ANY route: an action button, an outside tap, leaving the scene. */
  onDismiss?: () => void;
  /**
   * Two steps up from the default, both in GAME units, so they read the same everywhere rather
   * than only on phones.
   *
   *  - `'lg'` (~1.4×) for a card that IS the screen: the demo's end-of-run ask stops the game and
   *    asks for a decision, and at the default size that decision arrives in 19px type on a phone
   *    held at arm's length.
   *  - `'md'` (~1.2×) for a card that shares the screen. The end-of-trial card is not modal — the
   *    game-over screen behind it stays usable — so it must stay clear of the Menu/Replay row at
   *    y≈580, and `'lg'` would sit on top of it.
   */
  size?: 'md' | 'lg';
  /**
   * Runs on a click anywhere ON the card, not only on its buttons — the whole card becomes the
   * button.
   *
   * For a card whose every route leads to the same place, which is exactly the demo's end-of-run
   * card (src/game/ui/demoSaturnCard.ts): it makes one offer, so a player who taps the headline
   * instead of the button should get what they were reaching for rather than nothing. Never put
   * this on a card that also offers a way to decline — there, a stray tap would answer for them.
   * Buttons keep their own handlers; this fires in addition, so it must be idempotent.
   */
  onCardClick?: () => void;
}

/**
 * Vertical centre that clears the bottom button row every game-over screen shares (Summary's
 * Menu/Replay at y≈580, Celebration's at y≈600) — so the card never sits over the way out.
 */
const DEFAULT_Y = 360;

/**
 * The lowest edge a card may reach — what actually keeps the buttons behind it reachable.
 *
 * Measured, not guessed: the Summary screen's Menu/Replay pair occupies **y 544–616**. Centring
 * alone never cleared it. The default card is ~416 units tall, which centred puts its bottom at
 * ~568 — 24 units INTO the buttons — and because only the card takes pointer events, a tap that
 * lands in that strip hits the card and does nothing, while one just outside it falls through to
 * Summary's full-screen catcher and quits to the menu. That is the "Replay does nothing, or takes
 * me to the menu" report, and it predates any of the card sizes above.
 *
 * So a card is centred until it would cross this line and lifted after that. 536 leaves 8 units of
 * air above the buttons; the tallest card in any language (513) still starts at y≈23, clear of the
 * top edge.
 */
export const BOTTOM_LIMIT = 536;

/** Never push a tall card off the top edge either. */
export const TOP_MARGIN = 12;

/**
 * Where a card of `height` sits: centred on `preferredY` until that would put its bottom past
 * BOTTOM_LIMIT, then lifted; never pushed off the top.
 *
 * Exported and pure so the rule can be tested against real measured card heights without a
 * browser — see promoCard.placement.test.ts. The bug it exists to prevent is not visual: a card
 * that reaches into the button row silently eats the taps meant for Replay.
 */
export function promoCardY(height: number, preferredY: number = DEFAULT_Y): number {
  const half = height / 2;
  return Math.max(TOP_MARGIN + half, Math.min(preferredY, BOTTOM_LIMIT - half));
}

/**
 * Re-measure the card this often, this many times, after it appears (~1 s in all).
 *
 * Not paranoia: the card's height keeps changing after it is built. Webfonts swap in, the store
 * badge artwork lands, and the flag font is injected by JS on platforms whose emoji font has no
 * flags (src/platform/countryFlagFont.ts) — each can add a wrapped line. Measured once at build
 * time, the tallest French card ends up 16 units lower than it asked for; measured again over the
 * first second, it does not. A ResizeObserver covers the same ground where it exists, and this
 * covers the ResizeObserver being asleep in a background tab.
 */
const SETTLE_EVERY_MS = 120;
const SETTLE_TIMES = 8;

/**
 * How many cards are up, reflected onto `<body>` as `promo-card-open`.
 *
 * Read by exactly one thing: the desktop QR overlay hides while a card is showing (see the CSS
 * in index.html and src/game/ui/qrOverlay.ts). That overlay lives outside Phaser entirely, so a
 * body class is the cheapest honest signal — nothing has to be wired through the scenes, and the
 * state cannot go stale because it is decremented by the same `destroy` every card already fires.
 *
 * A counter and not a boolean: two cards can briefly overlap (one screen showing its prompt while
 * the previous scene's is still animating out), and a plain flag would be cleared by the first of
 * them to go.
 */
const CARD_OPEN_CLASS = 'promo-card-open';
let openCards = 0;

function markOpen(dom: Phaser.GameObjects.DOMElement): void {
  openCards += 1;
  document.body.classList.add(CARD_OPEN_CLASS);
  dom.once('destroy', () => {
    openCards = Math.max(0, openCards - 1);
    if (openCards === 0) document.body.classList.remove(CARD_OPEN_CLASS);
  });
}

export function showPromoCard(
  scene: Phaser.Scene,
  spec: PromoCardSpec,
  opts: PromoCardOptions = {},
): void {
  // Scene-owned timer: leaving the screen before it fires cancels it, so a prompt can never
  // surface over the next scene.
  scene.time.delayedCall(opts.delayMs ?? 0, () => build(scene, spec, opts));
}

function build(scene: Phaser.Scene, spec: PromoCardSpec, opts: PromoCardOptions): void {
  const card = document.createElement('div');
  card.className = opts.size ? `promo-card promo-card--${opts.size}` : 'promo-card';
  if (opts.className) card.classList.add(opts.className);
  card.setAttribute('role', 'dialog');
  card.setAttribute('aria-label', spec.title);
  // Taps on the card must not ALSO land on the screen behind it — on Summary that would mean
  // "Not now" quits to the menu. See domInput.
  sealFromPhaser(card);

  const title = document.createElement('div');
  title.className = 'promo-card__title';
  title.textContent = spec.title;
  card.append(title);

  // With several lines the first one carries the pitch and gets the star colour; a card with a
  // single line is just a sentence and should stay in the calm body style.
  spec.lines.forEach((line, i) => {
    const p = document.createElement('p');
    p.className = spec.lines.length > 1 && i === 0
      ? 'promo-card__line promo-card__line--lead'
      : 'promo-card__line';
    p.textContent = line;
    card.append(p);
  });

  if (spec.extra) card.append(spec.extra);

  const actions = document.createElement('div');
  actions.className = 'promo-card__actions';
  card.append(actions);

  const dom = scene.add.dom(GAME_WIDTH / 2, opts.y ?? DEFAULT_Y, card).setDepth(1000);
  markOpen(dom);

  // Height is only knowable from the laid-out element: it depends on the size, on how many lines
  // the card has, and on how many of those the player's language wraps onto two (the same card is
  // 448 units in Korean and 595 in French).
  //
  // And it is not knowable ONCE. The first measurement happens before the game's webfonts have
  // finished loading, and the card grows by a line or two when they arrive — measure only at build
  // time and a French card still ends up 70 units lower than it asked to be. Hence all three: place
  // it now, again when anything resizes it, and again after a beat for the browsers with no
  // ResizeObserver.
  const place = (): void => { dom.setY(promoCardY(card.offsetHeight, opts.y ?? DEFAULT_Y)); };
  place();
  const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place);
  observer?.observe(card);
  const settle = scene.time.addEvent({
    delay: SETTLE_EVERY_MS, repeat: SETTLE_TIMES, callback: place,
  });
  dom.once('destroy', () => { observer?.disconnect(); settle.remove(); });

  for (const action of spec.actions) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `promo-card__btn promo-card__btn--${action.kind}`;
    btn.textContent = action.label;
    btn.addEventListener('click', () => {
      action.onClick?.();
      if (!action.keepOpen) dom.destroy();
    });
    actions.append(btn);
  }

  if (opts.onCardClick) card.addEventListener('click', opts.onCardClick);

  // Event names as strings, never Phaser.GameObjects.Events.* — see the note on closeOnOutsideTap.
  if (opts.onDismiss) dom.once('destroy', opts.onDismiss);
  if (opts.closeOnOutsideTap) closeOnOutsideTap(scene, card, dom);

  // Entrance pop. Phaser owns the element's transform, so this must be a Phaser tween on the
  // GameObject — never a CSS transition on .promo-card (that would fight the DOM layer's own
  // positioning and slide the card in from the corner).
  dom.setAlpha(0).setScale(0.9);
  scene.tweens.add({ targets: dom, alpha: 1, scale: 1, duration: 260, ease: 'Back.easeOut' });
}

/**
 * Dismiss the card on a tap outside it.
 *
 * Bound on the window in the CAPTURE phase, not on the Phaser scene: the card shares the screen
 * with native buttons in the DOM layer (the menu's pills and Play button), and a canvas-only
 * listener would never see a tap that lands on one of those.
 *
 * Two things must not trip it: the tap that opened the card — still being dispatched when we get
 * here, hence the deferred bind — and taps on the card itself, including its own buttons.
 *
 * The event names are string literals on purpose. `Phaser` is imported here for its TYPES only, so
 * the transform drops the import entirely; reaching for a runtime constant like
 * `Phaser.GameObjects.Events.DESTROY` puts it back, and Phaser touches `window` at import time —
 * which breaks every node test that reaches this module (src/game/rating.test.ts found it).
 */
function closeOnOutsideTap(
  scene: Phaser.Scene,
  card: HTMLElement,
  dom: Phaser.GameObjects.DOMElement,
): void {
  let onDown: ((e: PointerEvent) => void) | null = null;
  const detach = (): void => {
    if (onDown) window.removeEventListener('pointerdown', onDown, true);
    onDown = null;
  };
  const bind = setTimeout(() => {
    onDown = (e: PointerEvent): void => {
      if (card.contains(e.target as Node)) return;
      detach();
      dom.destroy();
    };
    window.addEventListener('pointerdown', onDown, true);
  }, 0);

  // The listener outlives nothing: it goes with the card, however the card goes away.
  dom.once('destroy', () => { clearTimeout(bind); detach(); });
  scene.events.once('shutdown', () => { clearTimeout(bind); detach(); });
}
